// Bullet rendering functions - 弾丸描画処理


import { getBullets, getEnemyBullets } from '../../store/index';
import { getP5 } from '../../core/p5-instance';
import type { Bullet } from '../../types/game-types';

/**
 * drawPlayerBullets() - プレイヤーの弾丸描画
 * ホーミング弾、HUNTER弾、レーザー、BOMBER通常弾を描画
 */
export function drawPlayerBullets(): void {
    const p = getP5();
    for (const b of getBullets()) {
        // 非アクティブなホーミング弾は描画しない
        if (b.isHoming && b.homingLaunchActive === false) {
            continue;
        }
        // グローサイズ倍率（ホーミング弾用）
        const glowScale = b.homingGlowSize || 1.0;

        // 軌跡を描画
        for (let i = 0; i < b.trail.length; i++) {
            const alpha = p.map(i, 0, b.trail.length, 50, 255);
            const size = p.map(i, 0, b.trail.length, 2, 8) * (b.isHoming ? glowScale : 1);
            if (b.isHoming) {
                p.fill(255, 255, 255, alpha); // 白色
            } else if (b.isHunterBullet) {
                p.fill(200, 130, 60, alpha); // 落ち着いたオレンジ茶
            } else {
                p.fill(0, 255, 255, alpha);
            }
            p.circle(b.trail[i].x, b.trail[i].y, size);
        }

        if (b.isHoming) {
            drawHomingBullet(b, glowScale);
        } else if (b.isHunterBullet) {
            drawHunterBullet(b);
        } else if (b.isLaser) {
            drawLaserBullet(b);
        } else {
            drawBomberBullet(b);
        }
    }
}

/**
 * drawHomingBullet() - ホーミング弾の描画（ミサイル形状）
 */
function drawHomingBullet(b: Bullet, glowScale: number): void {
    const p = getP5();
    p.push();
    p.translate(b.x, b.y);
    p.rotate(p.atan2(b.vy, b.vx) + p.HALF_PI);
    p.scale(glowScale);

    // ブースター炎（後方に揺らぐ炎）
    const flicker = p.random(0.8, 1.3);
    const flameLen = 18 * flicker;
    // 外炎（オレンジ）
    p.fill(255, 80, 0, 200);
    p.noStroke();
    p.beginShape();
    p.vertex(-4, 8);
    p.vertex(0, 8 + flameLen);
    p.vertex(4, 8);
    p.endShape(p.CLOSE);
    // 中炎（黄色）
    p.fill(255, 180, 50, 230);
    p.beginShape();
    p.vertex(-3, 8);
    p.vertex(0, 8 + flameLen * 0.7);
    p.vertex(3, 8);
    p.endShape(p.CLOSE);
    // 内炎（白〜黄色）
    p.fill(255, 255, 150, 250);
    p.beginShape();
    p.vertex(-1.5, 8);
    p.vertex(0, 8 + flameLen * 0.4);
    p.vertex(1.5, 8);
    p.endShape(p.CLOSE);

    // グロー（明るい黄色）
    p.fill(255, 220, 50, 100 + glowScale * 30);
    p.noStroke();
    p.beginShape();
    p.vertex(0, -14);
    p.vertex(-6, -6);
    p.vertex(-6, 8);
    p.vertex(6, 8);
    p.vertex(6, -6);
    p.endShape(p.CLOSE);

    // 本体（ミサイル形状：先端 + 胴体）
    p.fill(255, 200, 50);
    p.stroke(255, 255, 150);
    p.strokeWeight(1.5);
    // 先端（三角形）
    p.beginShape();
    p.vertex(0, -12);
    p.vertex(-4, -4);
    p.vertex(4, -4);
    p.endShape(p.CLOSE);
    // 胴体（直線部分）
    p.rect(-4, -4, 8, 12, 1);

    // 尾翼（左右の小さな翼）
    p.fill(220, 160, 40);
    p.noStroke();
    p.beginShape();
    p.vertex(-4, 4);
    p.vertex(-7, 9);
    p.vertex(-4, 8);
    p.endShape(p.CLOSE);
    p.beginShape();
    p.vertex(4, 4);
    p.vertex(7, 9);
    p.vertex(4, 8);
    p.endShape(p.CLOSE);

    // 中心ライン（白く輝く）
    p.fill(255, 255, 255);
    p.noStroke();
    p.rect(-1, -8, 2, 10, 1);
    p.pop();
}

/**
 * drawHunterBullet() - HUNTER通常弾の描画
 */
function drawHunterBullet(b: Bullet): void {
    const p = getP5();
    p.push();
    p.translate(b.x, b.y);

    // グロー（抑えめ）
    p.fill(200, 120, 50, 60);
    p.beginShape();
    p.vertex(0, -10);
    p.vertex(-6, 0);
    p.vertex(0, 10);
    p.vertex(6, 0);
    p.endShape(p.CLOSE);

    // 本体（茶〜オレンジ）
    p.fill(220, 140, 60);
    p.stroke(255, 180, 100);
    p.strokeWeight(1);
    p.beginShape();
    p.vertex(0, -8);
    p.vertex(-4, 0);
    p.vertex(0, 8);
    p.vertex(4, 0);
    p.endShape(p.CLOSE);

    // 中心
    p.fill(255, 220, 150);
    p.noStroke();
    p.circle(0, 0, 3);
    p.pop();
}

/**
 * drawLaserBullet() - LANCERレーザー弾の描画
 */
function drawLaserBullet(b: Bullet): void {
    const p = getP5();
    const laserLen = 40;
    const w = b.laserWidth || 0;

    // 外側グロー
    p.fill(200, 50, 255, 60);
    p.noStroke();
    p.rect(b.x - w, b.y - laserLen, w * 2, laserLen + 10, 3);

    // 中間グロー
    p.fill(200, 100, 255, 120);
    p.rect(b.x - w * 0.7, b.y - laserLen + 5, w * 1.4, laserLen, 2);

    // コアビーム
    p.fill(255, 200, 255);
    p.rect(b.x - w * 0.4, b.y - laserLen + 8, w * 0.8, laserLen - 8, 2);

    // 先端のフラッシュ
    p.fill(255, 255, 255, 200);
    p.ellipse(b.x, b.y - laserLen, w * 0.8, 6);
}

/**
 * drawBomberBullet() - BOMBER通常弾の描画
 */
function drawBomberBullet(b: Bullet): void {
    const p = getP5();
    // 弾のグロー
    p.fill(0, 255, 255, 100);
    p.circle(b.x, b.y, 15);

    // 弾本体
    p.fill(0, 255, 255);
    p.circle(b.x, b.y, 8);

    // 弾の中心（白）
    p.fill(255);
    p.circle(b.x, b.y, 4);
}

/**
 * drawEnemyBullets() - 敵の弾丸描画
 * 通常弾、追尾弾、弾幕、拡散弾を描画
 */
export function drawEnemyBullets(): void {
    const p = getP5();
    for (const b of getEnemyBullets()) {
        // グロー効果
        p.fill(p.red(b.color), p.green(b.color), p.blue(b.color), 80);
        p.circle(b.x, b.y, b.size * 2);

        // 弾本体
        p.fill(b.color);
        p.stroke(255);
        p.strokeWeight(1);

        if (b.type === 'homing') {
            // 追尾弾は特殊な形状（三角形）
            p.push();
            p.translate(b.x, b.y);
            p.rotate(p.atan2(b.vy, b.vx) + p.HALF_PI);
            p.beginShape();
            p.vertex(0, -b.size / 2);
            p.vertex(-b.size / 2, b.size / 2);
            p.vertex(b.size / 2, b.size / 2);
            p.endShape(p.CLOSE);
            p.pop();
        } else if (b.type === 'barrage') {
            // 弾幕は小さな菱形
            p.push();
            p.translate(b.x, b.y);
            p.rotate(p.frameCount * 0.1);
            p.beginShape();
            p.vertex(0, -b.size / 2);
            p.vertex(b.size / 2, 0);
            p.vertex(0, b.size / 2);
            p.vertex(-b.size / 2, 0);
            p.endShape(p.CLOSE);
            p.pop();
        } else {
            // 通常弾・拡散弾は円形
            p.circle(b.x, b.y, b.size);
        }

        // 中心の白い点
        p.fill(255, 255, 255, 200);
        p.noStroke();
        p.circle(b.x, b.y, b.size / 3);
    }
}
